import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import React, { useState } from "react";
import { User } from "lucide-react";
import { supabase } from "@/ajax/clients/supabase";
import { Link } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SharingProfileModal } from "@/features/profile/components/SharingProfileModal";
import { ProfileAvatarImage } from "@/features/profile/components/profile-avatar-image";
import { Avatar, AvatarFallback } from "../ui/avatar";
import posthog from "posthog-js";
import type { User as UserType } from "@/common/types/user";
import { queryClient } from "@/ajax/queryClient";
export const UserAvatar = ({
  minimal = false,
  user,
}: {
  minimal?: boolean;
  user: UserType;
}) => {
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);

  const onSignOutClick = async () => {
    posthog.capture("manual_sign_out");
    queryClient.clear();
    const { error: _error } = await supabase.auth.signOut({
      scope: "local",
    });
  };

  const supportEmail =
    import.meta.env.VITE_SUPPORT_EMAIL || "hello@saileapp.com";

  const handleSupportClick = () => {
    setDropdownOpen(false);
    setShowSupportDialog(true);
  };

  const handleDialogClose = () => {
    setShowSupportDialog(false);
    setDropdownOpen(false);
  };

  return (
    <>
      <Dialog open={showSupportDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] sm:w-full">
          <DialogHeader className="pr-8">
            <DialogTitle>Support Contact</DialogTitle>
          </DialogHeader>
          <p className="text-center py-4">
            Please email us at:{" "}
            <span className="font-medium">{supportEmail}</span>
          </p>
        </DialogContent>
      </Dialog>

      <SharingProfileModal
        open={showSharingModal}
        onOpenChange={setShowSharingModal}
        userId={user.id}
      />

      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="rounded-full rounded-md h-auto p-0"
          >
            <Avatar className="cursor-pointer h-11 w-11">
              <ProfileAvatarImage
                userId={user.id}
                profileImagePath={user.profile_image_path}
                alt={
                  user.first_name
                    ? `${user.first_name} ${user.last_name || ""}`
                    : "Profile"
                }
              />
              <AvatarFallback className="bg-gray-700">
                {user.first_name && user.last_name ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 200 200"
                    className="w-full h-full"
                  >
                    <text
                      x="100"
                      y="125"
                      fontFamily="Arial, sans-serif"
                      fontSize="80"
                      textAnchor="middle"
                      fill="#ffffff"
                    >
                      {`${user.first_name[0]}${user.last_name[0]}`}
                    </text>
                  </svg>
                ) : (
                  <User className="w-5 h-5" color="white" />
                )}
              </AvatarFallback>
            </Avatar>

            <span className="sr-only">Toggle user menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="truncate">
            {user.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {!minimal && (
            <DropdownMenuItem asChild>
              <Link to="/provider/settings" className="w-full">
                Settings
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleSupportClick}>
            Support
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.location.reload()}>
            Refresh
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onClick={onSignOutClick}>
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
